/**
 * Cookie utilities for secure cookie management
 * Replaces localStorage for auth tokens and user preferences
 */

interface CookieOptions {
  maxAge?: number; // in seconds
  expires?: Date;
  path?: string;
  domain?: string;
  secure?: boolean;
  sameSite?: 'strict' | 'lax' | 'none';
  httpOnly?: boolean; // Note: can't be set via JS, only server-side
}

/**
 * Set a cookie with secure defaults
 */
export function setCookie(
  name: string,
  value: string,
  options: CookieOptions = {}
): void {
  const {
    maxAge = 86400 * 7, // 7 days default
    expires,
    path = '/',
    domain,
    secure = true,
    sameSite = 'lax'
  } = options;

  let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;

  if (maxAge && !expires) {
    cookieString += `; Max-Age=${maxAge}`;
  }

  if (expires) {
    cookieString += `; Expires=${expires.toUTCString()}`;
  }

  if (path) {
    cookieString += `; Path=${path}`;
  }

  if (domain) {
    cookieString += `; Domain=${domain}`;
  }

  // Only add Secure flag if we're on HTTPS or localhost
  if (secure && (window.location.protocol === 'https:' || window.location.hostname === 'localhost')) {
    cookieString += '; Secure';
  }

  if (sameSite) {
    cookieString += `; SameSite=${sameSite}`;
  }

  document.cookie = cookieString;
}

/**
 * Get a cookie value by name
 */
export function getCookie(name: string): string | null {
  const nameEQ = encodeURIComponent(name) + '=';
  const cookies = document.cookie.split(';');
  
  for (let cookie of cookies) {
    cookie = cookie.trim();
    if (cookie.indexOf(nameEQ) === 0) {
      return decodeURIComponent(cookie.substring(nameEQ.length));
    }
  }
  
  return null;
}

/**
 * Delete a cookie
 */
export function deleteCookie(name: string, path: string = '/'): void {
  setCookie(name, '', {
    maxAge: -1,
    path
  });
}

/**
 * Check if a cookie exists
 */
export function hasCookie(name: string): boolean {
  return getCookie(name) !== null;
}

/**
 * Get all cookies as an object
 */
export function getAllCookies(): Record<string, string> {
  const cookies: Record<string, string> = {};
  const cookiesArray = document.cookie.split(';');
  
  for (let cookie of cookiesArray) {
    cookie = cookie.trim();
    const [name, value] = cookie.split('=');
    if (name) {
      cookies[decodeURIComponent(name)] = decodeURIComponent(value || '');
    }
  }
  
  return cookies;
}

/**
 * Migrate localStorage data to cookies
 */
export function migrateFromLocalStorage(): void {
  // Migrate language preference
  const language = localStorage.getItem('language');
  if (language && !hasCookie('language')) {
    setCookie('language', language, {
      maxAge: 86400 * 365, // 1 year
      sameSite: 'lax'
    });
  }

  // Migrate auth state
  const isLoggedIn = localStorage.getItem('isLoggedIn');
  if (isLoggedIn && !hasCookie('isLoggedIn')) {
    setCookie('isLoggedIn', isLoggedIn, {
      maxAge: 86400 * 7, // 7 days
      sameSite: 'strict'
    });
  }

  // Note: Supabase tokens should be migrated carefully
  // This is handled in the auth configuration
}

/**
 * Auth-specific cookie helpers
 */
export const authCookies = {
  setAuthState(isLoggedIn: boolean): void {
    setCookie('isLoggedIn', String(isLoggedIn), {
      maxAge: 86400 * 7, // 7 days
      sameSite: 'strict'
    });
  },

  getAuthState(): boolean {
    return getCookie('isLoggedIn') === 'true';
  },

  clearAuthState(): void {
    deleteCookie('isLoggedIn');
  },

  setUserRole(email: string, role: string): void {
    // Store role with a hashed key for security
    const key = `role_${btoa(email).substring(0, 10)}`;
    setCookie(key, role, {
      maxAge: 86400 * 7, // 7 days
      sameSite: 'strict'
    });
  },

  getUserRole(email: string): string | null {
    const key = `role_${btoa(email).substring(0, 10)}`;
    return getCookie(key);
  },

  clearUserRole(email: string): void {
    const key = `role_${btoa(email).substring(0, 10)}`;
    deleteCookie(key);
  },

  getAuthToken(): string | null {
    // Check for Supabase auth token in cookies
    // The key is 'sb-auth-token' as defined in supabase.ts
    const authToken = getCookie('sb-auth-token');
    if (authToken) {
      try {
        // Parse the token to check if it's valid JSON
        const parsed = JSON.parse(authToken);
        if (parsed && parsed.access_token) {
          return authToken;
        }
      } catch {
        // If it's not valid JSON, it might be the raw token
        return authToken;
      }
    }
    return null;
  }
};

/**
 * Language preference cookie helpers
 */
export const langCookies = {
  setLanguage(lang: 'ar' | 'en'): void {
    setCookie('language', lang, {
      maxAge: 86400 * 365, // 1 year
      sameSite: 'lax'
    });
  },

  getLanguage(): string {
    return getCookie('language') || 'ar';
  },

  clearLanguage(): void {
    deleteCookie('language');
  }
};

/**
 * Clear all app cookies
 */
export function clearAllAppCookies(): void {
  const cookies = getAllCookies();
  const appCookieNames = ['isLoggedIn', 'language'];
  
  // Clear known app cookies
  appCookieNames.forEach(name => {
    if (name in cookies) {
      deleteCookie(name);
    }
  });
  
  // Clear role cookies (they start with 'role_')
  Object.keys(cookies).forEach(name => {
    if (name.startsWith('role_')) {
      deleteCookie(name);
    }
  });
}